<template>
  <div class="job-modify f-c" v-loading="loadingJob">
    <basic-form
      ref="jobForm"
      title="Job Info"
      :mode="FormModes.SPARSE"
      :form-params="jobForm"
      :value-change-cb="jobFormChange"
      :show-clear="true"
    />
    <job-content-create
      v-if="jobType === UnloadingJob && !loadingJob"
      ref="jobContentCreate"
      :goodQuery="goodQuery"
      :groupsChanged="groupsChanged"
      class="job-content-create"
      :original-goods-group-arr="originalGoodsGroupArr"
    />
    <job-content-create-with-pallet
      ref="jobContentCreateWithPallet"
      :job-type="jobType"
      :job-pallet-ids="jobPalletIds"
      v-else-if="[InboundJob, OutboundJob].includes(jobType) && !loadingJob"
    />
    <common-card
      title="Job Content"
      class="job-content-card"
      v-if="jobType === UnloadingJob"
    >
      <job-content
        :job-content="jobContent"
        :has-actions="false"
        :job-status="jobStatus"
      />
    </common-card>
    <div class="buttons f-r">
      <el-button type="danger" size="small" @click="cancelUpdate">
        Cancel Update
      </el-button>
      <el-button type="primary" size="small" @click="doUpdateJob">
        Update Job
      </el-button>
    </div>
  </div>
</template>
<script>
import isEmpty from 'lodash/isEmpty'
import get from 'lodash/get'
import cloneDeep from 'lodash/cloneDeep'
// models
import { FormModes, Tag } from '@/core/configs/types'
import { GoodsGroupWrapUp, JobModifyForm } from '@/core/models/job'
import {
  UnloadingJob,
  InboundJob,
  OutboundJob,
  JobCompleted,
  JobProcessing
} from '@/core/models/constant'
// apis
import { detailOrder } from '@/api/order'
// components
import CommonCard from '@/components/common/CommonCard.vue'
import JobContent from '@/components/jobs/JobContent'
import BasicForm from '@/components/common/BasicForm.vue'
import JobContentCreate from '@/components/jobs/JobContentCreate.vue'
import JobContentCreateWithPallet from '@/components/jobs/JobContentCreateWithPallet.vue'
// apis
import { detailJob, modifyJob } from '@/api/job'
export default {
  name: 'JobModify',
  components: {
    BasicForm,
    CommonCard,
    JobContent,
    JobContentCreate,
    JobContentCreateWithPallet
  },
  data() {
    return {
      FormModes,
      Tag,
      UnloadingJob,
      InboundJob,
      OutboundJob,
      loadingJob: false,
      originJob: null,
      originOrder: null,
      jobForm: cloneDeep(JobModifyForm),
      jobContent: [],
      originalGoodsGroupArr: []
    }
  },
  computed: {
    jobNo() {
      return this.$route.query?.jobNo
    },
    jobId() {
      return this.originJob?.id ?? ''
    },
    jobType() {
      return this.originJob?.type ?? ''
    },
    jobStatus() {
      return this.originJob?.status
    },
    jobPalletIds() {
      return this.originJob?.processingPalletIds ?? []
    },
    containerId() {
      return this.originOrder?.containerId ?? ''
    },
    goodQuery() {
      return { containerId: this.containerId }
    }
  },
  methods: {
    jobFormChange() {},
    resetJobBasicInputs() {
      Object.keys(this.jobForm).forEach(key => {
        this.jobForm[key].value = get(this.originJob, key)
        this.jobForm[key].default = this.jobForm[key].value
      })
    },
    doUpdateJob() {
      // first get job content
      // then pack up job info
      let passed, jobInfo
      ;[passed, jobInfo] = this.$refs.jobForm.validateAllInputs()
      if (!passed) return
      let goodsGroups = []
      let selectedPallets = []
      switch (this.jobType) {
        case UnloadingJob:
          passed = this.$refs.jobContentCreate.checkGroupValid()
          if (!passed) {
            this.$message({
              type: 'error',
              message: 'you have goods in packing list to be categorized!'
            })
            return
          }
          goodsGroups = this.jobContent.map(group => {
            return {
              ...group,
              addressId: group.address?.id,
              goods: group.goodsList.map(one => one.id),
              goodsList: undefined,
              goodsIds: undefined,
              address: undefined
            }
          })
          break
        case InboundJob:
        case OutboundJob:
          selectedPallets =
            this.$refs.jobContentCreateWithPallet.getSelectedPalletIds()
          break
      }
      this.loadingJob = true
      modifyJob({
        id: this.jobId,
        assigneeId: jobInfo.assigneeId,
        goodsGroups: goodsGroups,
        selectedPallets: selectedPallets
      })
        .then(res => {
          if (res) {
            this.$message({
              type: 'success',
              message: 'Successfully modified job!'
            })
            this.$router.back()
          }
        })
        .finally(() => {
          this.loadingJob = false
        })
    },
    cancelUpdate() {
      this.$router.back()
    },
    async getJobDetail() {
      this.loadingJob = true
      const jobDetail = await detailJob(this.jobNo)
      if (!jobDetail) {
        this.$message.error('Failed to get job detail!')
        return
      }
      if ([JobCompleted, JobProcessing].includes(jobDetail.status)) {
        this.$messsage.error(`Cannot edit ${jobDetail.status} job!`)
        this.$router.back()
        return
      }
      this.$set(this, 'originalGoodsGroupArr', jobDetail.goodsGroups ?? [])
      const orderDetail = await detailOrder(jobDetail.orderId)
      if (!orderDetail) {
        this.$message.error('Failed to get order detail!')
        return
      }
      this.$set(this, 'originJob', jobDetail)
      this.$set(this, 'originOrder', orderDetail)
      this.resetJobBasicInputs()
      this.loadingJob = false
    },
    groupsChanged(goodsGroups, goodsMap) {
      this.jobContent = Object.values(goodsGroups).map(group => {
        return GoodsGroupWrapUp(
          group,
          isEmpty(goodsMap) ? [] : group.goodsIds.map(key => goodsMap[key])
        )
      })
    }
  },
  mounted() {
    this.loadingJob = true
    this.getJobDetail()
  }
}
</script>
<style lang="scss">
@import '@/assets/styles/common.scss';

.el-loading-mask {
  background-color: rgba($global-background-color, 0.8) !important;
}
</style>
<style lang="scss" scoped>
@import '@/assets/styles/common.scss';

.job-modify {
  .job-content-create {
    @extend .global-margin;
  }

  .buttons {
    gap: 10px;
    justify-content: end;
    margin-right: 20px;
    margin-bottom: 40px;
  }
}
</style>
